import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { IrsMaximums, IrsMaximumsCoverageTypes, IrsMaximumsFrequencyTypes, IrsMaximumsTypes } from '../transaction.types';

interface IrsMaximumsParams {
  coverageLevel?: IrsMaximumsCoverageTypes;
  years?: number[];
  type?: string;
}

export const QUERY_KEY = 'useGetIrsMaximumsQueryKey';
export const useGetIrsMaximums = (values?: IrsMaximumsParams) => {
  const params = useMemo(() => {
    if (!values) return undefined;

    const queryGenerator = new URLSearchParams();

    if (values?.coverageLevel) {
      queryGenerator.set('coverage_level', values.coverageLevel);
    }

    if (values?.type) {
      queryGenerator.set('type', `${values?.type}_MAX`);
    }

    if (values?.years) {
      values.years.forEach((year) => {
        queryGenerator.append('tax_year', year.toString());
      });
    }

    return queryGenerator;
  }, [values]);

  const { data, isLoading, isSuccess } = useQuery([QUERY_KEY, values], () =>
    api.get(API_URL.IRS_MAXIMUMS, { params })
  );

  const formattedData: IrsMaximums[] = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item) => ({
      id: `${item?.id || ''}`,
      type: item?.type as unknown as IrsMaximumsTypes,
      taxYear: `${item?.tax_year || ''}`,
      amount: item?.amount || 0,
      frequencyType: item?.frequency_type as unknown as IrsMaximumsFrequencyTypes,
      irsCoverageLevelType: item?.irs_coverage_level_type as unknown as IrsMaximumsCoverageTypes,
      taxYearEnd: (item?.tax_year_end || '') as unknown as string,
    }));
  }, [data]);

  const lastDayForContribution = useMemo(() => {
    if (!data?.data) return undefined;

    return data.data.find((item) => item?.tax_year === dayjs().year() - 1)?.tax_year_end as unknown as string;
  }, [data]);

  return {
    lastDayForContribution,
    formattedData,
    isLoading,
    data: data?.data,
    isSuccess,
  };
};
